import type { HTMLAttributes, ReactNode } from "react";

/**
 * The one scroll body for every side surface of the studio: both rails, every
 * left panel (Advanced and Basic), the Advanced inspector, AI Match and
 * Fine-tune. It must sit in a flex COLUMN whose height is fixed (the surfaces
 * are `absolute top-0 bottom-0`); it then takes exactly the room left under the
 * surface's header and scrolls whatever does not fit.
 *
 * Three things it guarantees, so no panel has to remember them:
 *
 * 1. **It shrinks, its children do not.** `min-h-0 flex-1` lets the scroller
 *    shrink below its content (a flex item's default `min-height: auto` would
 *    grow it past the window instead, and the bottom of the panel would be cut
 *    off by the surface's `overflow-hidden` with nothing to scroll). Its direct
 *    children are `shrink-0`: in a flex-column scroller, a child with
 *    `overflow-hidden` loses its automatic minimum height and gets squeezed to
 *    fit — clipped content, and no scrollbar, because nothing overflows.
 * 2. **No sideways jump.** The thin bar keeps its gutter (`scrollbar-gutter:
 *    stable`), so opening a section that makes the panel overflow does not
 *    re-wrap every row 6px narrower.
 * 3. **It says when there is more.** A 6px bar is easy to miss and the rails
 *    hide theirs, so on a short window a list that went on simply looked cut
 *    off. `trackScrollEdges` marks the scroller `data-more-above` /
 *    `data-more-below`, and a soft fade shows on that edge (`.scroll-fade` in
 *    global.css). The fades are SIBLINGS of the scroller rather than a mask on
 *    it: a mask would make the scroller a stacking context and clip anything a
 *    row draws outside it (the section menu's fixed click-catcher, for one).
 */
export function ScrollArea({
  children,
  className = "",
  bar = "thin",
  ...rest
}: {
  children: ReactNode;
  /** Classes for the scrolling element itself — padding, flex direction, gap. */
  className?: string;
  /** `thin`: the studio's quiet 6px bar. `none`: no bar at all (the rails, where
   *  the fade alone says the list goes on). */
  bar?: "thin" | "none";
} & Omit<HTMLAttributes<HTMLDivElement>, "className" | "children">) {
  const barClass =
    bar === "thin" ? "scroll-thin [scrollbar-gutter:stable]" : "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
  return (
    <div className="relative flex min-h-0 flex-1 flex-col" data-scroll-area={bar}>
      <div
        ref={trackScrollEdges}
        className={`${barClass} min-h-0 flex-1 overflow-y-auto overscroll-contain [&>*]:shrink-0 ${className}`}
        {...rest}
      >
        {children}
      </div>
      <div aria-hidden className="scroll-fade" data-edge="top" />
      <div aria-hidden className="scroll-fade" data-edge="bottom" />
    </div>
  );
}

/**
 * Ref callback: keep `data-more-above` / `data-more-below` on a scroller in
 * step with its scroll position and its content. Attributes, not React state —
 * scrolling re-renders nothing.
 *
 * The scroller's own box rarely changes; its CONTENT does (a section opens, a
 * library grid loads, an image arrives), so the direct children are observed
 * too, and re-collected whenever they are swapped.
 */
export function trackScrollEdges(el: HTMLDivElement | null): (() => void) | undefined {
  if (!el) return undefined;
  const update = () => {
    const max = el.scrollHeight - el.clientHeight;
    // 1px of slack: at a fractional zoom the last scroll position can stop a
    // fraction short of `max`, and the fade must still clear.
    el.toggleAttribute("data-more-above", el.scrollTop > 1);
    el.toggleAttribute("data-more-below", max - el.scrollTop > 1);
  };
  update();
  el.addEventListener("scroll", update, { passive: true });
  if (typeof ResizeObserver === "undefined" || typeof MutationObserver === "undefined") {
    return () => el.removeEventListener("scroll", update);
  }
  const ro = new ResizeObserver(update);
  const watch = () => {
    ro.disconnect();
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
  };
  watch();
  const mo = new MutationObserver(() => {
    watch();
    update();
  });
  mo.observe(el, { childList: true });
  return () => {
    el.removeEventListener("scroll", update);
    ro.disconnect();
    mo.disconnect();
  };
}
